import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "./Navbar";
import NavbarWithPushContent from "./NavbarWithPushContent";
import Footer from "./Footer";
import LoadingSpinner from "../Assets/LoadingSpinner";
import Seo, { SITE_URL } from "../Assets/Seo";
import { BlogPost } from "../Types/Types";
import { useTranslation } from "react-i18next";

const stripHtml = (html?: string) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || "").replace(/\s+/g, " ").trim();
};

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface BlogDetailProps {
  products: ProductCategory[];
  lang: string;
}

const BlogDetail: React.FC<BlogDetailProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/blog/${id}`);
        if (res.ok) setItem(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, [id, apiUrl]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-montserrat">
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>
      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      <main className="flex-grow px-4 pt-28 md:pt-32 pb-16">
        {loading ? (
          <LoadingSpinner />
        ) : item ? (
          <article className="max-w-3xl mx-auto">
            <Seo
              title={item.title?.[lang]}
              description={stripHtml(item.content?.[lang]).slice(0, 160)}
              image={item.image}
              type="article"
              lang={lang}
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                headline: item.title?.[lang],
                image: item.image ? [item.image] : undefined,
                datePublished: item.createdAt,
                dateModified: item.updatedAt || item.createdAt,
                author: { "@type": "Organization", name: "Galafarm", url: SITE_URL },
                publisher: {
                  "@type": "Organization",
                  name: "Galafarm",
                  logo: { "@type": "ImageObject", url: `${SITE_URL}/GalafarmLogo.png` },
                },
              }}
            />
            <Link to="/blog" className="text-brand-500 hover:text-brand-600 font-medium">
              ← {t("blog.header")}
            </Link>
            <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-gray leading-tight">
              {item.title?.[lang]}
            </h1>
            {item.createdAt && (
              <p className="mt-2 text-sm text-gray-400">
                {new Date(item.createdAt).toLocaleDateString()}
              </p>
            )}
            {item.image && (
              <img
                src={item.image}
                alt={item.title?.[lang]}
                className="mt-6 w-full rounded-2xl shadow-lg object-cover"
              />
            )}
            <div
              className="prose max-w-none mt-8 text-gray-700 ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: item.content?.[lang] || "" }}
            />
          </article>
        ) : (
          <p className="text-center text-gray-600 text-lg mt-12">{t("blog.empty")}</p>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogDetail;
